import React, { useState, useEffect, useRef, useCallback } from 'react';
import { logger } from '../utils/logger';
import {
  Box,
  Grid,
  CardContent,
  Typography,
  Button,
  Alert,
  Menu,
  MenuItem,
  IconButton,
  Paper,
  Divider,
  useTheme
} from '@mui/material';
import { AreaChart, Area, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { alpha } from '@mui/material/styles';
import {
  TrendingUp,
  CreditCard,
  People,
  Analytics,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Speed as SpeedIcon,
  Timeline as TimelineIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useNavigate } from 'react-router-dom';
import StatsCard from '../components/Dashboard/StatsCard';
import ModernCard from '../components/Common/ModernCard';
import ModernLoader from '../components/Common/ModernLoader';
import IconContainer from '../components/Common/IconContainer';
import TierUsage from '../components/Common/TierUsage';
import TierUpgradeProgress from '../components/Common/TierUpgradeProgress';
import ResponsivePageHeader from '../components/Common/ResponsivePageHeader';
import useResponsive from '../hooks/useResponsive';
import { analyticsAPI, AnalyticsOverview } from '../services/api';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { handleApiError } = useErrorHandler();
  const navigate = useNavigate();
  const theme = useTheme();
  const { isMobile, getTouchFriendlySize } = useResponsive();

  const [analytics, setAnalytics] = useState<AnalyticsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [createPassMenuAnchor, setCreatePassMenuAnchor] = useState<null | HTMLElement>(null);

  // Ref to prevent double API calls in StrictMode
  const hasLoadedRef = useRef(false);

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await analyticsAPI.getOverview();
      setAnalytics(data);
    } catch (err) {
      logger.error('Error loading analytics:', err);
      const errorResult = handleApiError(err as any, 'Greška prilikom učitavanja dashboard analitike');
      setError(errorResult.message);
    } finally {
      setLoading(false);
    }
  }, [handleApiError]);

  useEffect(() => {
    if (hasLoadedRef.current) return;

    // Clear any stale cache from localStorage (legacy) and sessionStorage
    localStorage.removeItem('dashboard_analytics');
    localStorage.removeItem('dashboard_last_load_time');
    sessionStorage.removeItem('dashboard_analytics');
    sessionStorage.removeItem('dashboard_last_load_time');

    hasLoadedRef.current = true;
    loadAnalytics();
  }, [loadAnalytics]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getWelcomeMessage = () => {
    const hour = new Date().getHours();
    if (hour < 4) return 'Dobro veče';
    if (hour < 12) return 'Dobro jutro';
    if (hour < 18) return 'Dobar dan';
    return 'Dobro veče';
  };

  // Calculate MoM change percentages
  const momTotalPasses = analytics?.mom_change?.total_passes || 0;
  const momInstalledPasses = analytics?.mom_change?.installed_passes || 0;

  const formatChange = (value: number) => {
    if (value === 0) return '0%';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const stats = [
    {
      title: 'Kreirani Pass-ovi',
      value: (analytics?.total_passes || 0).toLocaleString(),
      change: formatChange(momTotalPasses),
      isPositive: momTotalPasses >= 0,
      color: 'primary' as const,
      icon: <CreditCard />
    },
    {
      title: 'Instalirani Pass-ovi',
      value: (analytics?.active_passes || 0).toLocaleString(),
      change: formatChange(momInstalledPasses),
      isPositive: momInstalledPasses >= 0,
      color: 'success' as const,
      icon: <People />
    },
    {
      title: 'Stopa Instalacije',
      value: analytics?.total_passes ? `${((analytics.active_passes / analytics.total_passes) * 100).toFixed(1)}%` : '0%',
      change: 'N/A',
      isPositive: true,
      color: 'info' as const,
      icon: <TrendingUp />
    },
    {
      title: 'Validirani Pass-ovi',
      value: (analytics?.invalidated_passes || 0).toLocaleString(),
      change: 'N/A', // No MoM for invalidated yet
      isPositive: true,
      color: 'warning' as const,
      icon: <Analytics />
    }
  ];

  const quickActions = [
    {
      title: 'Kreiraj Novi Pass',
      description: 'Stvori novu člansku karticu ili kupon',
      icon: <AddIcon />,
      color: 'primary' as const,
      action: (event?: React.MouseEvent<HTMLElement>) => {
        // Open dropdown menu for pass type selection
        if (event) {
          setCreatePassMenuAnchor(event.currentTarget);
        }
      },
      variant: 'contained' as const
    },
    {
      title: 'Detaljnu Analitiku',
      description: 'Pregled sveobuhvatnih izveštaja',
      icon: <TimelineIcon />,
      color: 'primary' as const,
      action: () => navigate('/analytics'),
      variant: 'outlined' as const
    },
    {
      title: 'Kontaktiraj Podršku',
      description: 'Pošalji support ticket ili pozovi nas',
      icon: <EmailIcon />,
      color: 'primary' as const,
      action: () => navigate('/support'),
      variant: 'outlined' as const
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <ModernLoader size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={loadAnalytics}>
              Pokušaj ponovo
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{
      p: { xs: 2, sm: 2.5, md: 3 },
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    }}>
      {/* Header Section */}
      <ResponsivePageHeader
        title={`${getWelcomeMessage()}! 👋`}
        subtitle="Evo pregleda vaše OmniPass aktivnosti"
        gradient={true}
        sx={{
          '& .MuiTypography-h4, & .MuiTypography-h5, & .MuiTypography-h6': {
            color: 'white !important',
            background: 'none !important',
            backgroundClip: 'unset !important',
            WebkitBackgroundClip: 'unset !important',
            WebkitTextFillColor: 'white !important',
            textShadow: '0 2px 4px rgba(0,0,0,0.2) !important'
          }
        }}
        userInfo={{
          name: user?.email?.split('@')[0] || 'Korisnik',
          email: user?.email,
          company: user?.company_name || 'OmniPass Portal',
          role: user?.role
        }}
        actions={
          <IconButton
            onClick={handleMenuOpen}
            sx={{
              color: 'white',
              minHeight: getTouchFriendlySize(44),
              minWidth: getTouchFriendlySize(44)
            }}
          >
            <MoreVertIcon />
          </IconButton>
        }
        showUserAvatar={true}
        showDate={true}
        showCompany={true}
      />

      {/* Stats Cards */}
      <Grid container spacing={{ xs: 2, md: 3 }} mb={{ xs: 3, md: 4 }}>
        {stats.map((stat) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={stat.title}>
            <StatsCard
              title={stat.title}
              value={stat.value}
              change={stat.change}
              color={stat.color}
              icon={stat.icon}
              isPositive={stat.isPositive}
            />
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions & Tier Usage Section */}
      <Grid container spacing={{ xs: 2, md: 3 }} mb={{ xs: 3, md: 4 }}>
        {/* Quick Actions */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <ModernCard sx={{ height: '100%' }}>
            <CardContent sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              p: { xs: 2, sm: 2.5, md: 3 }
            }}>
              <Box display="flex" alignItems="center" gap={{ xs: 1.5, md: 2 }} mb={{ xs: 2, md: 3 }}>
                <IconContainer color="primary" variant="soft">
                  <SpeedIcon />
                </IconContainer>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{ fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' } }}
                >
                  Brze Akcije
                </Typography>
              </Box>

              <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ flex: 1 }}>
                {quickActions.map((action) => (
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={action.title}>
                    <Paper sx={{
                      p: { xs: 2, sm: 2.5, md: 3 },
                      height: '100%',
                      minHeight: { xs: 100, sm: 120 },
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      '&:active': {
                        transform: 'scale(0.98)',
                        transition: 'transform 0.1s ease'
                      }
                    }}
                    onClick={(e) => action.action(e)}
                    >
                      <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
                        <IconContainer
                          color={action.color}
                          variant="soft"
                          sx={{
                            mb: isMobile ? 1.5 : 2,
                            width: getTouchFriendlySize(48),
                            height: getTouchFriendlySize(48)
                          }}
                        >
                          {action.icon}
                        </IconContainer>
                        <Typography
                          variant={isMobile ? "body1" : "subtitle1"}
                          fontWeight={600}
                          mb={1}
                          sx={{ fontSize: isMobile ? '1rem' : '1.1rem' }}
                        >
                          {action.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontSize: isMobile ? '0.875rem' : '0.925rem',
                            lineHeight: 1.4
                          }}
                        >
                          {action.description}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </ModernCard>
        </Grid>

        {/* Tier Usage */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '100%' }}>
            <ModernCard>
              <TierUsage
                compact={false}
                showUpgradeButton={true}
                autoRefreshInterval={0}
              />
            </ModernCard>

            {/* Tier Upgrade Progress */}
            <TierUpgradeProgress />
          </Box>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={{ xs: 2, md: 3 }} mb={{ xs: 3, md: 4 }}>
        {/* 30-Day Trend Chart */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <ModernCard>
            <CardContent>
              <Box display="flex" alignItems="center" gap={{ xs: 1.5, md: 2 }} mb={{ xs: 2, md: 3 }}>
                <IconContainer color="info" variant="soft">
                  <TimelineIcon />
                </IconContainer>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{ fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' } }}
                >
                  Trend Kreiranja Pass-ova (Poslednjih 30 Dana)
                </Typography>
              </Box>

              {analytics?.chart_data && analytics.chart_data.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                  <AreaChart data={analytics.chart_data}>
                    <defs>
                      <linearGradient id="colorPasses" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.1)} />
                    <XAxis
                      dataKey="date"
                      stroke={theme.palette.text.secondary}
                      style={{ fontSize: isMobile ? '11px' : '12px' }}
                    />
                    <YAxis
                      stroke={theme.palette.text.secondary}
                      style={{ fontSize: isMobile ? '11px' : '12px' }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="created"
                      stroke={theme.palette.primary.main}
                      fillOpacity={1}
                      fill="url(#colorPasses)"
                      name="Kreirani Pass-ovi"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height={250}>
                  <Typography color="text.secondary">Nema dostupnih podataka</Typography>
                </Box>
              )}
            </CardContent>
          </ModernCard>
        </Grid>

        {/* Device Breakdown Chart */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <ModernCard>
            <CardContent>
              <Box display="flex" alignItems="center" gap={{ xs: 1.5, md: 2 }} mb={{ xs: 2, md: 3 }}>
                <IconContainer color="success" variant="soft">
                  <Analytics />
                </IconContainer>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{ fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' } }}
                >
                  Distribucija po Uređajima
                </Typography>
              </Box>

              {analytics && (analytics.apple_wallet > 0 || analytics.google_wallet > 0) ? (
                (() => {
                  const deviceData = [
                    { name: 'iOS (Apple Wallet)', value: analytics.apple_wallet, color: theme.palette.info.main },
                    { name: 'Android (Google Wallet)', value: analytics.google_wallet, color: theme.palette.success.main }
                  ];

                  const renderLegendContent = (props: any) => {
                    const { payload } = props;

                    // Pre-compute values to reduce nesting depth
                    const legendItems = payload?.map((entry: any, index: number) => {
                      const deviceValue = deviceData.find((d: any) => d.name === entry.value)?.value || 0;
                      return { entry, index, deviceValue };
                    }) || [];

                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: 3,
                          flexWrap: 'wrap',
                          px: 2
                        }}
                      >
                        {legendItems.map(({ entry, index, deviceValue }: { entry: any; index: number; deviceValue: number }) => (
                          <Box
                            key={`legend-${index}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 2,
                              backgroundColor: alpha(entry.color, 0.08),
                              border: `1px solid ${alpha(entry.color, 0.2)}`,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: alpha(entry.color, 0.15),
                                transform: 'translateY(-2px)',
                                boxShadow: `0 4px 12px ${alpha(entry.color, 0.2)}`
                              }
                            }}
                          >
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: entry.color,
                                boxShadow: `0 0 8px ${alpha(entry.color, 0.4)}`
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: 'text.primary'
                              }}
                            >
                              {entry.value}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontWeight: 600
                              }}
                            >
                              {deviceValue}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    );
                  };

                  return (
                    <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
                      <PieChart>
                        <Pie
                          data={deviceData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={isMobile ? 70 : 90}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {deviceData.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '8px'
                          }}
                        />
                        <Legend
                          verticalAlign="bottom"
                          height={70}
                          iconType="circle"
                          wrapperStyle={{
                            paddingTop: '20px',
                            paddingBottom: '16px'
                          }}
                          content={renderLegendContent}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  );
                })()
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height={280}>
                  <Typography color="text.secondary">Nema dostupnih podataka</Typography>
                </Box>
              )}
            </CardContent>
          </ModernCard>
        </Grid>
      </Grid>

      {/* 2FA Security section removed - Entra ID handles MFA */}

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        disableScrollLock={true}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              zIndex: 1300,
              '& .MuiMenuItem-root': {
                px: 2,
                py: 1.5,
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08)
                }
              }
            }
          }
        }}
      >
        <MenuItem onClick={() => { navigate('/account'); handleMenuClose(); }}>
          Podešavanja naloga
        </MenuItem>
        <MenuItem onClick={() => { navigate('/admin/audit-logs'); handleMenuClose(); }}>
          Audit logovi
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { loadAnalytics(); handleMenuClose(); }}>
          Osveži podatke
        </MenuItem>
      </Menu>

      {/* Create Pass Type Menu */}
      <Menu
        anchorEl={createPassMenuAnchor}
        open={Boolean(createPassMenuAnchor)}
        onClose={() => setCreatePassMenuAnchor(null)}
        transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        disableScrollLock={true}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              minWidth: 200,
              zIndex: 1300,
              '& .MuiMenuItem-root': {
                px: 2,
                py: 1.5,
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08)
                }
              }
            }
          }
        }}
      >
        <MenuItem onClick={() => {
          navigate('/membership-cards');
          setCreatePassMenuAnchor(null);
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            <CreditCard fontSize="small" />
            <Typography>Člansku Karticu</Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={() => {
          navigate('/coupons');
          setCreatePassMenuAnchor(null);
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            <TrendingUp fontSize="small" />
            <Typography>Kupon</Typography>
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Dashboard;
